import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { ReactNode } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LinkOther } from '@sorare/core/src/atoms/navigation/Box';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { So5RankingRewards } from 'components/ranking/So5RankingRewards';
import { RewardMultiplier } from 'components/rewards/AnyRewardConfig/RewardMultiplier';
import { Variant } from 'components/rewards/RewardOverview/types';
import {
  ContenderLeftFooterState,
  getContenderLeftFooterState,
} from 'lib/contender';

import type { SimpleContenderFooterStatus_so5LeaderboardContender } from './__generated__/index.graphql';

const messages = defineMessages({
  scoreToTarget: {
    id: 'Lineup.LiveLineupFooter.scoreToTarget',
    defaultMessage: '<b>{score}</b> to target',
  },
  scoreToClosestTarget: {
    id: 'Lineup.LiveLineupFooter.RightLiveLineupFooter.scoreToClosestTarget',
    defaultMessage: '<b>{score}</b> to closest target',
  },
  scoreToClosestReward: {
    id: 'Lineup.LiveLineupFooter.scoreToClosestReward',
    defaultMessage: '<b>{score}</b> to closest reward',
  },
});

const StyledBold = styled.b`
  color: var(--c-white);
`;

type Props = {
  so5LeaderboardContender: SimpleContenderFooterStatus_so5LeaderboardContender;
};

export const SimpleContenderFooterStatus = ({
  so5LeaderboardContender,
}: Props) => {
  const { so5Lineup } = so5LeaderboardContender;
  const so5Ranking = so5Lineup?.so5Rankings.at(0);
  const leftState = getContenderLeftFooterState(so5LeaderboardContender);
  if (leftState === null || !so5Ranking) {
    return null;
  }

  const { state } = leftState;

  switch (state) {
    case ContenderLeftFooterState.CANCELLED:
      return <Ellipsis>{so5Lineup?.cancelledReason}</Ellipsis>;
    case ContenderLeftFooterState.NOT_STARTED:
      return (
        <FormattedMessage
          id="Lineup.LiveLineupFooter.timeLeftToNextMatch"
          defaultMessage="<b>{timeLeft}</b> to next match"
          values={{
            b: (chunks: ReactNode[]) => {
              return <StyledBold>{chunks}</StyledBold>;
            },
            timeLeft: <TimeLeft time={leftState.date} />,
          }}
        />
      );
    case ContenderLeftFooterState.REWARDS:
      return (
        so5Ranking && (
          <LinkOther as="div">
            <Horizontal gap={0.5}>
              <So5RankingRewards
                variant={Variant.INLINE}
                so5Ranking={so5Ranking}
              />
              {so5Lineup && <RewardMultiplier lineup={so5Lineup} />}
            </Horizontal>
          </LinkOther>
        )
      );
    case ContenderLeftFooterState.POINTS_TO_CLOSEST_REWARD:
      return (
        <FormattedMessage
          {...messages.scoreToClosestReward}
          values={{
            b: (chunks: ReactNode[]) => {
              return <StyledBold>{chunks}</StyledBold>;
            },
            score: (
              <FormattedMessage
                {...fantasy.pointsWithoutFormat}
                values={{
                  score: leftState.points,
                }}
              />
            ),
          }}
        />
      );
    case ContenderLeftFooterState.POINTS_TO_SAFETY:
      return (
        <FormattedMessage
          id="Lineup.LiveLineupFooter.scoreToSafety"
          defaultMessage="<b>{score}</b> to safety"
          values={{
            b: (chunks: ReactNode[]) => {
              return <StyledBold>{chunks}</StyledBold>;
            },
            score: (
              <FormattedMessage
                {...fantasy.pointsWithoutFormat}
                values={{
                  b: StyledBold,
                  score: leftState.points,
                }}
              />
            ),
          }}
        />
      );
    case ContenderLeftFooterState.POINTS_TO_TARGET:
    case ContenderLeftFooterState.POINTS_TO_THRESHOLD:
      return (
        <FormattedMessage
          {...(state === ContenderLeftFooterState.POINTS_TO_TARGET
            ? messages.scoreToTarget
            : messages.scoreToClosestTarget)}
          values={{
            b: (chunks: ReactNode[]) => {
              return <StyledBold>{chunks}</StyledBold>;
            },
            score: (
              <FormattedMessage
                {...fantasy.pointsWithoutFormat}
                values={{
                  b: StyledBold,
                  score: leftState.points,
                }}
              />
            ),
          }}
        />
      );
    default:
      return null;
  }
};

SimpleContenderFooterStatus.fragments = {
  so5LeaderboardContender: gql`
    fragment SimpleContenderFooterStatus_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Lineup {
        id
        rewardMultiplier
        cancelledReason
        so5Rankings {
          id
          ...So5RankingRewards_so5Ranking
        }
        ...RewardMultiplier_lineup
      }
      ...getContenderLeftFooterState_so5LeaderboardContender
    }
    ${getContenderLeftFooterState.fragments.so5LeaderboardContender}
    ${So5RankingRewards.fragments.so5Ranking}
    ${RewardMultiplier.fragments.lineup}
  ` as TypedDocumentNode<SimpleContenderFooterStatus_so5LeaderboardContender>,
};
